import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  formatDistanceInMiles,
  formatDistanceInMilesOrMeters,
} from "@src/appV2/lib/utils/distance";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import {
  CbhIcon,
  DotSeparatedList,
  ReviewsSummary,
  Text,
} from "@src/appV2/redesign/system/ui-components";
import { useHistory } from "react-router-dom";

import { type PlacementWithDetails } from "../../../api/useFetchPaginatedPlacements";
import { getPlacementDetailsPath } from "../../../paths";
import { getCardType } from "../../../utils/card-type";
import { PlacementSectionTemplate } from "./PlacementSectionTemplate";

interface WorkplaceInfoSectionProps {
  isReviewModeEnabled: boolean;
  ratingAggregates?: {
    avgRating: number;
    numberOfRatings: number;
  };
  placement: PlacementWithDetails;
  placementCandidate: Pick<PlacementCandidate, "id" | "worker">;
}

export function WorkplaceInfoSection(props: WorkplaceInfoSectionProps) {
  const { isReviewModeEnabled, ratingAggregates, placement, placementCandidate } = props;

  const history = useHistory();
  const cardType = getCardType();

  const goToWorkplaceProfile = () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
      action: "workplace_profile_button_clicked",
      placementId: placement.id,
      placementCandidateId: placementCandidate.id,
      source: "placement_details_page",
      workerId: placementCandidate.worker.id,
      workplaceId: placement.workplace.id,
    });
    history.push(
      `/home-v2/placements/workplaces/${placement.workplace.id}/profile?sourcePlacementId=${placement.id}`
    );
  };

  const goToWorkplaceLocation = () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
      action: "view_location_clicked",
      placementId: placement.id,
      placementCandidateId: placementCandidate.id,
      source: "placement_details_page",
      workerId: placementCandidate.worker.id,
      workplaceId: placement.workplace.id,
    });
    history.replace(
      getPlacementDetailsPath(placement.id, placementCandidate.id, {
        cardType,
        section: "workplaceLocation",
      })
    );
  };

  return (
    <Stack direction="row" spacing={4} width="100%">
      <PlacementSectionTemplate
        title={placement.workplace.name}
        iconType="building"
        onTitleClick={goToWorkplaceProfile}
      >
        {isReviewModeEnabled ? (
          <Stack spacing={2}>
            {(isDefined(placement.workplace.type) || isDefined(placement.distance)) && (
              <DotSeparatedList
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {isDefined(placement.workplace.type) && (
                  <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.primary })}>
                    {placement.workplace.type}
                  </Text>
                )}
                {isDefined(placement.distance) && (
                  <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.primary })}>
                    {formatDistanceInMilesOrMeters(placement.distance, "long")}
                  </Text>
                )}
              </DotSeparatedList>
            )}
            {isDefined(ratingAggregates) && (
              <ReviewsSummary
                rating={ratingAggregates.avgRating}
                ratingsCount={ratingAggregates.numberOfRatings}
                ratingFormat="detailed"
                size="small"
              />
            )}
            {isDefined(placement.distance) && (
              <Box onClick={goToWorkplaceLocation}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    width: "fit-content",
                    paddingBottom: 1,
                    borderBottom: (theme) => `1px solid ${theme.palette.border?.primary}`,
                  }}
                >
                  <CbhIcon type="map-pin" size="small" />
                  <Text variant="body2">View location</Text>
                </Stack>
              </Box>
            )}
          </Stack>
        ) : (
          isDefined(placement.distance) && (
            <DotSeparatedList
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                {formatDistanceInMiles(placement.distance, "long")}
              </Text>
              <Box onClick={goToWorkplaceLocation}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    paddingBottom: 1,
                    borderBottom: (theme) => `1px solid ${theme.palette.border?.primary}`,
                  }}
                >
                  <CbhIcon type="map-pin" size="small" />
                  <Text variant="body2">View location</Text>
                </Stack>
              </Box>
            </DotSeparatedList>
          )
        )}
      </PlacementSectionTemplate>
      <IconButton
        variant="outlined"
        iconType="chevron-right"
        size="xSmall"
        sx={{ width: 24, height: 24 }}
        onClick={goToWorkplaceProfile}
      />
    </Stack>
  );
}
